import type {
  ApiClientErrorCode,
  PaintCategory,
  PaintListItem,
  PaintTone,
} from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { PaintFilterChips, type PaintFilterField } from './PaintFilterChips';
import { PaintResultCard } from './PaintResultCard';

/**
 * Presenter Phase 3 (Каталог красок). См. ADR-0005 + ADR-0010.
 *
 * Чистая функция от props к JSX. Никаких useEffect / fetch / useNavigate.
 * 5 состояний (P-021): idle / loading / success / empty / error.
 *
 * Отличия от DecorsCatalogView:
 *   - Hardcoded title «Краски» (один paint-домен — нет kind picker'а наверху).
 *   - 2 chip-группы (tones + categories) — без brands/types.
 *   - VideoButton НЕ ставим: Phase 3 — без KindPicker preface; «первый экран
 *     функции» semantics неоднозначны для paint (см. CLAUDE.md §16.4 — paint
 *     не имеет отдельного entry-screen). Уточнение трекается в **B-045**.
 */
export type PaintsCatalogState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface PaintsCatalogViewProps {
  state: PaintsCatalogState;
  /** Только при state='success' / 'empty' */
  items?: readonly PaintListItem[];
  /** Cursor следующей страницы. null → нет данных или достигнут конец. */
  nextCursor: string | null;
  /** Только при state='error' */
  error?: { code: ApiClientErrorCode; message: string };

  // Текущий выбор chip'ов (preview, до клика «Найти»)
  selectedTones: readonly PaintTone[];
  selectedCategories: readonly PaintCategory[];

  onToggle: (field: PaintFilterField, value: string) => void;
  onResetGroup: (field: PaintFilterField) => void;
  onSearch: () => void;
  onResetAll: () => void;
  onLoadMore: () => void;
  onItemClick: (globalId: string) => void;
}

export function PaintsCatalogView(props: PaintsCatalogViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Краски" />

      {/* Filter panel — всегда видна. */}
      <section className="border-b border-icon-border p-6 flex flex-col gap-6">
        <PaintFilterChips
          selectedTones={props.selectedTones}
          selectedCategories={props.selectedCategories}
          onToggle={props.onToggle}
          onResetGroup={props.onResetGroup}
        />
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={props.onSearch}
            aria-label="Найти краски по выбранным фильтрам"
            className={[
              'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
              'font-bold text-[18px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Найти
          </button>
          <button
            type="button"
            onClick={props.onResetAll}
            aria-label="Сбросить выбранные фильтры"
            className={[
              'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
              'text-[16px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Сбросить фильтр
          </button>
        </div>
      </section>

      {/* Results area — switch on state */}
      <section className="flex-1 p-4 overflow-y-auto">
        <StateView {...props} />
      </section>

      <BottomBar />
    </main>
  );
}

function StateView(props: PaintsCatalogViewProps) {
  switch (props.state) {
    case 'idle':
      return <IdleState />;
    case 'loading':
      return <LoadingState />;
    case 'success':
      return (
        <div className="flex flex-col gap-5">
          <p className="text-muted text-base m-0">Найдено: {props.items?.length ?? 0}</p>
          <div className="flex flex-col gap-5">
            {(props.items ?? []).map((item) => (
              <PaintResultCard key={item.globalId} item={item} onItemClick={props.onItemClick} />
            ))}
          </div>
          {props.nextCursor !== null && (
            <button
              type="button"
              onClick={props.onLoadMore}
              aria-label="Показать ещё"
              className={[
                'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
                'text-[16px] py-3',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
              ].join(' ')}
            >
              Показать ещё
            </button>
          )}
        </div>
      );
    case 'empty':
      return <EmptyState onResetAll={props.onResetAll} />;
    case 'error':
      return <ErrorState message={props.error?.message ?? ''} />;
  }
}

function IdleState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
        Выберите фильтры и нажмите «Найти»
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function EmptyState({ onResetAll }: { onResetAll: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">
        По выбранным фильтрам ничего не нашли. Попробуйте сменить фильтры.
      </p>
      <button
        type="button"
        onClick={onResetAll}
        aria-label="Сбросить все фильтры"
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Сбросить все фильтры
      </button>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
    </div>
  );
}
